import { useMemo, type KeyboardEvent } from "react";
import type { LlmEntry, MagiMethod } from "../config";
import type { ModelError, ModelResponse, Phase, StructuredResult } from "../types";
import { ModelNode } from "./ModelNode";
import { MethodAnimation } from "./MethodAnimation";

/**
 * Ring geometry — the ring sits below the chamber's geometric centre
 * (cy=54) so the status-bar strip at the top clears the top node by ~10 px
 * even in the tightest (3-node) configuration. LAYOUT_R_BASE=28 keeps the
 * bottom node (at cy + r + node_half ≈ 99.6 %) from clipping the chamber
 * border. Effective vertical band: cy ± r → [26, 82] %. We still grow the
 * ring (up to LAYOUT_R_MAX = 42) for large cohorts and shrink tiles via
 * the --node-scale CSS var.
 */
const LAYOUT_CY = 54;
const LAYOUT_R_BASE = 28;
const LAYOUT_R_MAX = 42;
const CROWD_THRESHOLD = 8;
/** Where the node ring ends on the chamber edge so converge lines can stop short
 *  of the central core (leaves the "rapporteur" label readable). */
const CORE_R = 10;

function layoutRadiusFor(n: number): number {
  return Math.min(LAYOUT_R_MAX, LAYOUT_R_BASE + Math.max(0, n - CROWD_THRESHOLD) * 1.2);
}
function nodeScaleFor(n: number): number {
  return Math.max(0.7, Math.min(1, 1 - Math.max(0, n - CROWD_THRESHOLD) * 0.03));
}

export function DeliberationChamber({
  phase,
  statusMessage,
  selectedLlms,
  availableLlms,
  method,
  deliberative,
  result,
  onSelectModel,
  selectedModel,
}: {
  phase: Phase;
  statusMessage: string;
  selectedLlms: string[];
  availableLlms: LlmEntry[];
  method: MagiMethod;
  deliberative: boolean;
  result: StructuredResult | null;
  onSelectModel: (model: string | null) => void;
  selectedModel: string | null;
}) {
  const llmMeta = useMemo(
    () => new Map(availableLlms.map((l) => [l.id, l])),
    [availableLlms],
  );

  const round = result?.rounds?.at(-1);
  const responses: ModelResponse[] = round?.responses ?? [];
  const roundErrors: ModelError[] = round?.errors ?? [];
  const responseByModel = useMemo(() => {
    const m = new Map<string, ModelResponse>();
    for (const r of responses) m.set(r.model, r);
    return m;
  }, [responses]);
  const errorByModel = useMemo(() => {
    const m = new Map<string, ModelError>();
    for (const e of roundErrors) m.set(e.model, e);
    return m;
  }, [roundErrors]);

  const modelIds = selectedLlms.length > 0
    ? selectedLlms
    : [...responses.map((r) => r.model), ...roundErrors.map((e) => e.model)];

  const N = Math.max(modelIds.length, 2);
  const radius = layoutRadiusFor(N);
  const nodeScale = nodeScaleFor(N);
  const positions = useMemo(() => layoutCircle(N, radius), [N, radius]);

  const hasResults = phase === "responses" || phase === "aggregating" ||
    phase === "rapporteur" || phase === "complete" || phase === "crosstalk";

  const nodeActive = phase !== "idle" && phase !== "dispatch";
  const showCore = phase === "aggregating" || phase === "rapporteur" || phase === "complete";
  const coreDone = phase === "complete";

  return (
    <div
      className="chamber"
      aria-label="Deliberation chamber"
      data-phase={phase}
      style={{ ["--node-scale" as string]: String(nodeScale) }}
    >
      <div className="status-bar">
        <span style={{ color: "var(--text-dim)" }}>
          <span className="mono" style={{ marginRight: 6, opacity: 0.6 }}>
            {phaseGlyph(phase)}
          </span>
          {statusMessage}
        </span>
      </div>

      <svg className="links" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
        {phase === "dispatch" &&
          positions.map((p, i) => (
            <line
              key={`d-${i}`}
              className="dispatch-line"
              x1={50}
              y1={14}
              x2={p.x}
              y2={p.y}
            />
          ))}
        {phase === "crosstalk" && deliberative &&
          crossPairs(positions).map((pair, i) => (
            <line
              key={`c-${i}`}
              className="crosstalk-line"
              x1={pair.a.x}
              y1={pair.a.y}
              x2={pair.b.x}
              y2={pair.b.y}
            />
          ))}
        {(phase === "aggregating" || phase === "rapporteur" || phase === "complete") &&
          positions.map((p, i) => {
            const end = pullTowardCenter(p, CORE_R);
            return (
              <line
                key={`r-${i}`}
                className="converge-line"
                x1={p.x}
                y1={p.y}
                x2={end.x}
                y2={end.y}
              />
            );
          })}
      </svg>

      <MethodAnimation
        method={method}
        phase={phase}
        responses={responses}
      />

      {modelIds.map((id, i) => {
        const pos = positions[i];
        const meta = llmMeta.get(id);
        const resp = responseByModel.get(id);
        const err = errorByModel.get(id);
        return (
          <ModelNode
            key={id}
            id={id}
            label={meta?.label ?? id}
            codename={codenameFor(i)}
            x={pos.x}
            y={pos.y}
            phase={phase}
            active={nodeActive}
            response={resp}
            error={err}
            hasResult={hasResults}
            method={method}
            selected={selectedModel === id}
            onClick={() =>
              onSelectModel(selectedModel === id ? null : id)
            }
          />
        );
      })}

      <div
        className={`core${showCore ? " active" : ""}${coreDone ? " done" : ""}${method === "Synthesis" && showCore && !coreDone ? " synthesis" : ""}`}
        {...(coreDone
          ? {
              role: "button",
              tabIndex: 0,
              "aria-label": "Jump to rapporteur report",
              title: "Jump to rapporteur report",
              onClick: jumpToRapporteurReport,
              onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  jumpToRapporteurReport();
                }
              },
            }
          : { "aria-hidden": true })}
      >
        MAGI
        {coreDone && (
          <span className="core-jump-hint" aria-hidden>
            ⌄
          </span>
        )}
      </div>
    </div>
  );
}

function jumpToRapporteurReport() {
  const target = document.getElementById("rapporteur-report");
  if (!target) return;
  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView({
    behavior: reduce ? "auto" : "smooth",
    block: "start",
  });
}

function layoutCircle(
  n: number,
  r: number = LAYOUT_R_BASE,
): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  const cx = 50, cy = LAYOUT_CY;
  for (let i = 0; i < n; i++) {
    // Start at top and go clockwise.
    const theta = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    out.push({ x: cx + r * Math.cos(theta), y: cy + r * Math.sin(theta) });
  }
  return out;
}

/** Pull an endpoint toward the ring centre (cx=50, cy=LAYOUT_CY) by `pad`
 *  percent, so a converge line stops short of the rapporteur core instead of
 *  running under the label. */
function pullTowardCenter(p: { x: number; y: number }, pad: number) {
  const dx = 50 - p.x;
  const dy = LAYOUT_CY - p.y;
  const d = Math.hypot(dx, dy);
  if (d === 0) return { x: 50, y: LAYOUT_CY };
  const t = (d - pad) / d;
  return { x: p.x + dx * t, y: p.y + dy * t };
}

function crossPairs<T>(pts: T[]): { a: T; b: T }[] {
  const out: { a: T; b: T }[] = [];
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) out.push({ a: pts[i], b: pts[j] });
  }
  return out;
}

function phaseGlyph(phase: Phase): string {
  switch (phase) {
    case "dispatch":    return "⟐";
    case "processing":  return "⬢";
    case "responses":   return "⬢";
    case "crosstalk":   return "⇄";
    case "aggregating": return "◈";
    case "rapporteur":  return "✎";
    case "complete":    return "✓";
    case "failed":      return "⚠";
    default:            return "◌";
  }
}

function codenameFor(i: number): string {
  // Neutral codenames (avoid Evangelion character names).
  // Latin A–Z so every position renders consistently; numeric fallback
  // past 26 participants.
  if (i < 26) return `NODE-${String.fromCharCode(65 + i)}`;
  return `NODE-${i + 1}`;
}
